import { JSX, ReactElement } from "react";
import { appContext } from "../../../AppContext";
import { Menu, IMenuItem as ISidebarDataMenuItem } from "./SidebarData";
import { MenuItemKey } from "./MenuItemKey";
import { PagedResponse } from "@iconics/core";
import { Solution } from "./DataModels";
import { DateTime } from "luxon";
import { routes } from "../../Navigation/Router/Routes";
import { IRouterProps, withRouting } from "@iconics/components";
import { matchPath } from "react-router";
import { List as SList } from "@iconics/components";
import { ListItem as SListItem } from "@iconics/components";
import "./Styles.scss";
import { SerendieSymbol } from "@serendie/symbols";
import { IbssComponent } from "../../Core/BaseComponent/IbssComponent";
import { ReactComponent as AdminPortalMenuHeroSvg } from "./Admin Portal Menu Hero.svg"

class Sidebar extends IbssComponent<IProps, IState>
{
    private pageCacheKey = '7268e50b-968a-4a59-8284-d2391ba198f6';

    constructor(props: IProps)
    {
        super(props);

        this.state =
        {
            menu: [],
            showAppMenu: false,
            expandedMenuItemIds: [],
            activeMenuItemId: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const menu = await appContext().inMemoryCache.lazyGet(this.pageCacheKey, DateTime.now().plus({ days: 1 }), () => this.loadMenu())
        this.setExpandedMenuItems();
    }

    public async componentDidUpdate(prevProps: IProps): Promise<void>
    {
        if (this.props.location.pathname != prevProps.location.pathname)
        {
            this.setExpandedMenuItems();
        }
    }

    private async loadMenu(): Promise<void>
    {
        const menu = new Menu();
        const staticMenu: IMenuItem[] = menu.items.filter(i => i.solutionId == null);
        let dynamicMenu: IMenuItem[] = [];
        const userCanReadSolutions = appContext().localStorageProvider.hasRight('DATAMODEL.Solutions.Read')
        if (userCanReadSolutions)
        {
            const solutions = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.get<PagedResponse<Solution[]>>({
                select: Solution,
                tenancy: appContext().tenantId,
                filter: `IsEnabled eq 'Yes' and ShowOnMenu eq 'Yes' and ApplicationUri ne null and ApplicationUri ne ''`,
                orderBy: 'OrderSequence,Name',
            });

            dynamicMenu = solutions.value.map(solution =>
            {
                const menuItem = menu.items.find(i => i.solutionId == solution.Id);

                return {
                    id: menuItem?.id ?? solution.Id,
                    solutionId: solution.Id,
                    label: solution.Name,
                    permissions: true,
                    icon: solution.IconUri || <SerendieSymbol name='placeholder' />,
                    children: menuItem?.children ?? [],
                    link: () => solution.ApplicationUri,
                } as IMenuItem;
            });
        }

        const completeMenu = [...staticMenu, ...dynamicMenu];
        await this.setStateAsync({ menu: completeMenu });
    }

    private setExpandedMenuItems(): void
    {
        const activeRoute = routes.find(i => matchPath({ path: i.path, end: true }, this.props.location.pathname));
        const activeMenuItemId = activeRoute?.menuItemId ?? '';
        const flattenedMenuItems = this.state.menu.flattenTree(i => i.id, i => i.children);
        const expandedMenuItems = flattenedMenuItems.ancestorsOf(activeMenuItemId, i => i.id, i => i.parentId, true).reverse();
        const expandedMenuItemIds = expandedMenuItems.slice(0, expandedMenuItems.length - 1).map(i => i.id);
        document.title = ['FADX', ...expandedMenuItems.map(i => i.label)].join(' | ');

        this.setState({
            activeMenuItemId: activeMenuItemId,
            expandedMenuItemIds: expandedMenuItemIds,
        });
    }

    private getMenuItemIcon(menuItem: IMenuItem): ReactElement
    {
        return (typeof menuItem.icon === 'string' ? <img src={menuItem.icon} /> : menuItem.icon);
    }

    private menuItemIdToString(id: MenuItemKey | string): string
    {
        return (typeof id == 'string' ? id : MenuItemKey[id]);
    }

    private handleAppClick(app: IMenuItem): void
    {
        const url = app.link() ?? '#';
        if (url.startsWith('http'))
        {
            window.location.href = url;
        }
        else
        {
            this.props.navigate(url);
        }
    }
    
    private handleMenuItemPress(event: React.KeyboardEvent, menuItem: IMenuItem): void
    {
        event.stopPropagation();
        if (event.key === 'Enter' || event.key === ' ')
        {
            this.handleNavigate(menuItem);
        }
    }

    private handleMenuItemClick(event: React.MouseEvent, menuItem: IMenuItem): void
    {
        event.stopPropagation();
        this.handleNavigate(menuItem);
    }

    private handleNavigate(menuItem: IMenuItem): void
    {
        const hasChildren = (menuItem.children && menuItem.children.length > 0);
        const isExpanded = this.state.expandedMenuItemIds.includes(menuItem.id);

        if (!hasChildren)
        {
            // navigate to item
            this.props.navigate(menuItem.link() ?? '#');
        }
        else if (isExpanded)
        {
            // collapse item
            const flattenedMenuItems = this.state.menu.flattenTree(i => i.id, i => i.children);
            const ancestorIds = flattenedMenuItems.ancestorsOf(menuItem.id, i => i.id, i => i.parentId, false).reverse().map(i => i.id);
            this.setState({ expandedMenuItemIds: ancestorIds });
            //console.log(ancestorIds.map(i => this.menuItemIdToString(i)));
        }
        else
        {
            // expand item
            const flattenedMenuItems = this.state.menu.flattenTree(i => i.id, i => i.children);
            const ancestorIdsAndSelf = flattenedMenuItems.ancestorsOf(menuItem.id, i => i.id, i => i.parentId, true).reverse().map(i => i.id);
            this.setState({ expandedMenuItemIds: ancestorIdsAndSelf });
            //console.log(ancestorIdsAndSelf.map(i => this.menuItemIdToString(i)));
        }
    }

    public render(): JSX.Element
    {
        const activeAppId = this.state.expandedMenuItemIds[0];
        const activeApp = this.state.menu.find(i => i.permissions && i.id === activeAppId);

        return (
            <SList
                className='fadx-sidebar'
            >
                {
                    activeApp &&
                    <>
                        <SListItem
                            className='fadx-sidebar__active-app'
                        >
                            <AdminPortalMenuHeroSvg
                                onClick={() => this.setState({ showAppMenu: !this.state.showAppMenu })}
                            />
                        </SListItem>
                        <a href="#main-content" className="skip-link" tabIndex={1}>
                            {appContext().labels.Platform_Accessibility_SkipToContent}
                        </a>
                        {
                            this.state.showAppMenu
                                ?
                                this.state.menu
                                    .filter(i => i.permissions)
                                    .map(app => this.renderApp(app))
                                :
                                activeApp.children
                                    .filter(i => i.permissions)
                                    .map(item => this.renderMenuItem(item, activeApp, 0))
                        }
                    </>
                }
                <SListItem
                    className='fadx-sidebar__logo'
                >
                    <img
                        alt=''
                        src='/images/Logos/fadx-logo.png'
                    />
                </SListItem>
            </SList>
        );
    }

    private renderApp(app: IMenuItem): JSX.Element
    {
        const icon = this.getMenuItemIcon(app);

        return (
            <SListItem
                key={this.menuItemIdToString(app.id)}
                className='fadx-sidebar__app'
                title={app.label}
                leftIcon={icon}
                onClick={() =>
                {
                    this.setState({ showAppMenu: false });
                    this.handleAppClick(app);
                }}
            />
        );
    }

    private renderMenuItem(menuItem: IMenuItem, parentItem: IMenuItem, level: number): JSX.Element
    {
        const isActive = (this.state.activeMenuItemId == menuItem.id);
        const hasChildren = (menuItem.children && menuItem.children.length > 0);
        const isExpanded = this.state.expandedMenuItemIds.includes(menuItem.id);
        const isParentExpanded = this.state.expandedMenuItemIds.includes(parentItem.id);
        const leftIcon = this.getMenuItemIcon(menuItem);
        const rightIcon = (hasChildren ? <SerendieSymbol name={isExpanded ? 'chevron-up' : 'chevron-down'} /> : undefined);
        const indentation = 16 + 24 * level;

        return (
            <>
                <SListItem
                    key={this.menuItemIdToString(menuItem.id)}
                    title={menuItem.label}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    onKeyDown={e => this.handleMenuItemPress(e, menuItem)}
                    onClick={e => this.handleMenuItemClick(e, menuItem)}
                    className={`fadx-sidebar__menu-item ${isActive ? 'fadx-sidebar__menu-item--active' : ''} ${isParentExpanded ? 'fadx-sidebar__menu-item--visible' : 'fadx-sidebar__menu-item--hidden'}`}
                    style={{ paddingLeft: indentation }}
                />
                {
                    menuItem.children
                        .filter(i => i.permissions)
                        .map(i => this.renderMenuItem(i, menuItem, level + 1))
                }
            </>
        );
    }
}

export default withRouting<IRawProps>(Sidebar);
export interface IProps extends IRawProps, IRouterProps { }
export interface IRawProps { }

export interface IState
{
    menu: Array<IMenuItem>;
    activeMenuItemId: MenuItemKey | string;
    expandedMenuItemIds: (MenuItemKey | string)[];
    showAppMenu: boolean;
}

export interface IMenuItem extends Omit<ISidebarDataMenuItem, 'id'>
{
    id: MenuItemKey | string;
}
